import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, ChevronDown, ChevronUp, HelpCircle, User, Calendar, CreditCard, Shield, Video, FileText, Home, MessageCircle } from 'lucide-react';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    { id: 'all', label: 'Toutes', icon: HelpCircle },
    { id: 'account', label: 'Compte', icon: User },
    { id: 'appointments', label: 'Rendez-vous', icon: Calendar },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'teleconsultation', label: 'Téléconsultation', icon: Video },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'homecare', label: 'Soins à domicile', icon: Home },
    { id: 'security', label: 'Sécurité', icon: Shield }
  ];

  const faqItems = [
    // Compte
    {
      id: 1,
      category: 'account',
      question: "Comment créer un compte sur DOCKHTA ?",
      answer: `Pour créer un compte DOCKHTA :
      
1. Cliquez sur "S'inscrire" sur la page d'accueil
2. Choisissez votre type de profil (Patient, Professionnel, Établissement)
3. Remplissez vos informations personnelles
4. Vérifiez votre email avec le code OTP reçu
5. Votre compte est prêt !

Vous pouvez également vous inscrire avec votre compte Google pour un accès plus rapide.`
    },
    {
      id: 2,
      category: 'account',
      question: "J'ai oublié mon mot de passe, que faire ?",
      answer: `Si vous avez oublié votre mot de passe :

1. Sur la page de connexion, cliquez sur "Mot de passe oublié"
2. Entrez votre adresse email
3. Vous recevrez un code de réinitialisation par email
4. Utilisez ce code pour créer un nouveau mot de passe

Le code est valide pendant 10 minutes. Si vous ne recevez pas l'email, vérifiez vos spams.`
    },
    {
      id: 3,
      category: 'account',
      question: "Comment modifier mes informations personnelles ?",
      answer: `Pour modifier vos informations :

1. Connectez-vous à votre compte
2. Allez dans "Mon profil" depuis le menu
3. Cliquez sur "Modifier" pour les informations que vous souhaitez changer
4. Enregistrez vos modifications

Vous pouvez modifier votre photo, numéro de téléphone, adresse, et d'autres informations à tout moment.`
    },
    // Rendez-vous
    {
      id: 4,
      category: 'appointments',
      question: "Comment prendre rendez-vous avec un médecin ?",
      answer: `Pour prendre rendez-vous :

1. Utilisez la barre de recherche pour trouver un professionnel
2. Filtrez par spécialité, localisation ou disponibilité
3. Consultez le profil du praticien
4. Choisissez un créneau disponible dans son calendrier
5. Confirmez votre rendez-vous

Vous recevrez une confirmation par email et une notification de rappel.`
    },
    {
      id: 5,
      category: 'appointments',
      question: "Comment annuler ou reporter un rendez-vous ?",
      answer: `Pour modifier un rendez-vous :

1. Allez dans "Mes rendez-vous"
2. Trouvez le rendez-vous concerné
3. Cliquez sur "Annuler" ou "Reporter"
4. Pour un report, choisissez un nouveau créneau

Important : Les annulations sont gratuites jusqu'à 24h avant le rendez-vous. Au-delà, des frais peuvent s'appliquer selon la politique du praticien.`
    },
    {
      id: 6,
      category: 'appointments',
      question: "Que faire si le médecin annule mon rendez-vous ?",
      answer: `Si votre rendez-vous est annulé par le praticien :

• Vous serez immédiatement notifié par email et dans l'application
• Tout paiement effectué sera automatiquement remboursé
• Vous pourrez reprendre rendez-vous avec le même praticien ou en choisir un autre
• Notre équipe support peut vous aider à trouver une alternative rapidement`
    },
    // Paiement
    {
      id: 7,
      category: 'payment',
      question: "Quels modes de paiement sont acceptés ?",
      answer: `DOCKHTA accepte les modes de paiement suivants :

• Mobile Money : Orange Money, MTN Mobile Money
• Cartes bancaires : Visa, Mastercard
• Paiement sur place : Possible pour certains rendez-vous en présentiel

Les paiements sont sécurisés et vous recevez toujours une confirmation par email.`
    },
    {
      id: 8,
      category: 'payment',
      question: "Comment obtenir un remboursement ?",
      answer: `Les remboursements sont effectués dans les cas suivants :

• Annulation avant 24h : Remboursement intégral automatique
• Annulation par le praticien : Remboursement intégral sous 48h
• Problème technique empêchant la consultation : Contactez le support

Les remboursements sont crédités sur le mode de paiement original sous 3 à 5 jours ouvrés.`
    },
    // Téléconsultation
    {
      id: 9,
      category: 'teleconsultation',
      question: "Comment fonctionne la téléconsultation ?",
      answer: `La téléconsultation sur DOCKHTA :

1. Prenez rendez-vous en choisissant "Téléconsultation"
2. À l'heure du rendez-vous, connectez-vous à votre compte
3. Cliquez sur "Rejoindre la consultation" depuis vos rendez-vous
4. La vidéo se lance automatiquement dans votre navigateur
5. Consultez votre médecin comme en cabinet

Assurez-vous d'avoir une bonne connexion internet et d'autoriser l'accès à votre caméra/micro.`
    },
    {
      id: 10,
      category: 'teleconsultation',
      question: "Que faire si j'ai des problèmes techniques pendant la consultation ?",
      answer: `En cas de problème technique :

1. Vérifiez votre connexion internet
2. Rafraîchissez la page
3. Essayez avec un autre navigateur (Chrome recommandé)
4. Si le problème persiste, utilisez le chat pour contacter le praticien

Si la consultation ne peut avoir lieu pour raisons techniques de notre côté, vous serez remboursé intégralement.`
    },
    // Documents
    {
      id: 11,
      category: 'documents',
      question: "Comment stocker mes documents médicaux ?",
      answer: `Pour stocker vos documents :

1. Allez dans "Mon dossier médical"
2. Cliquez sur "Ajouter un document"
3. Sélectionnez le type (ordonnance, résultat d'examen, etc.)
4. Téléchargez le fichier (PDF, image)
5. Le document est stocké de manière sécurisée

Vos documents sont chiffrés et accessibles uniquement par vous et les praticiens que vous autorisez.`
    },
    {
      id: 12,
      category: 'documents',
      question: "Comment partager mes documents avec un médecin ?",
      answer: `Pour partager un document :

1. Lors d'un rendez-vous, le praticien peut accéder à vos documents partagés
2. Vous pouvez aussi envoyer des documents via le chat sécurisé
3. Dans votre dossier, sélectionnez un document et cliquez "Partager"
4. Choisissez le praticien avec qui partager

Vous gardez le contrôle total sur qui peut voir vos documents médicaux.`
    },
    // Soins à domicile
    {
      id: 13,
      category: 'homecare',
      question: "Comment demander des soins à domicile ?",
      answer: `Pour demander des soins à domicile :

1. Allez dans "Soins à domicile"
2. Sélectionnez le type de soin nécessaire
3. Décrivez votre besoin
4. Indiquez votre adresse et disponibilités
5. Un professionnel vous sera proposé

Les types de soins disponibles incluent : injections, pansements, kinésithérapie, soins maternels, vaccination, etc.`
    },
    {
      id: 14,
      category: 'homecare',
      question: "Comment sont sélectionnés les professionnels pour les soins à domicile ?",
      answer: `Nos professionnels de soins à domicile :

• Sont tous vérifiés et certifiés
• Possèdent les diplômes et autorisations nécessaires
• Ont été formés aux protocoles DOCKHTA
• Sont couverts par une assurance professionnelle

Vous pouvez consulter les avis des autres patients avant de confirmer.`
    },
    // Sécurité
    {
      id: 15,
      category: 'security',
      question: "Mes données de santé sont-elles sécurisées ?",
      answer: `La sécurité de vos données est notre priorité :

• Chiffrement de bout en bout de toutes les données médicales
• Stockage sur serveurs sécurisés certifiés
• Accès strictement contrôlé et journalisé
• Conformité aux réglementations sur les données de santé
• Sauvegardes régulières

Consultez notre Politique de Confidentialité pour plus de détails.`
    },
    {
      id: 16,
      category: 'security',
      question: "Comment supprimer mon compte et mes données ?",
      answer: `Pour supprimer votre compte :

1. Allez dans "Paramètres" > "Mon compte"
2. Cliquez sur "Supprimer mon compte"
3. Confirmez votre choix

Note : Certaines données médicales doivent être conservées conformément à la loi (20 ans). Vous pouvez demander l'export de vos données avant suppression.`
    }
  ];

  const filteredFAQ = faqItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-[#7C3AED]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Questions Fréquentes
          </h1>
          <p className="text-slate-600">
            Trouvez rapidement des réponses à vos questions
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher une question..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 text-lg"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => {
            const IconComponent = cat.icon;
            return (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? "default" : "outline"}
                className={activeCategory === cat.id ? "bg-[#7C3AED] hover:bg-[#6D28D9]" : ""}
                onClick={() => setActiveCategory(cat.id)}
              >
                <IconComponent className="w-4 h-4 mr-2" />
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {filteredFAQ.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm overflow-hidden">
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full p-6 text-left flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-[#7C3AED]" />
                  </div>
                  <span className="font-medium text-slate-900">{item.question}</span>
                </div>
                {openItems[item.id] ? (
                  <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              {openItems[item.id] && (
                <CardContent className="px-6 pb-6 pt-0">
                  <div className="pl-12 text-slate-600 whitespace-pre-line leading-relaxed">
                    {item.answer}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {filteredFAQ.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun résultat trouvé pour votre recherche</p>
          </div>
        )}

        {/* Contact CTA */}
        <Card className="mt-12 border-0 shadow-sm bg-gradient-to-r from-[#7C3AED]/5 to-[#7C3AED]/10">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Vous n&apos;avez pas trouvé votre réponse ?
            </h3>
            <p className="text-slate-600 mb-6">
              Notre équipe support est disponible pour vous aider
            </p>
            <Link to="/contact">
              <Button className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                Contactez-nous
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FAQ;
